import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { LoadingTile } from '@carvana/merch-tile';
import { useContext } from 'react';
import VehicleTile from '@/home/VehicleTile';
import TrackWrapper from '~/components/common/TrackWrapper';
import Sections from '~/constants/home/sections';
import AvailabilityAndPriceContext from '~/context/home/AvailabilityAndPriceContext';
import RecentVehiclesContext from '~/context/home/RecentVehiclesContext';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import includesEvVehicle from '~/utilities/includesEvVehicle';
import someHaveAllDealTags from '~/utilities/someHaveAllDealTags';
import Carousel, {
  BottomPaddingForBoxShadow,
  CarouselSkeleton
} from '../Carousel';

function RecentlyViewed() {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    recentVehiclesFromSearch: { data: recentVehicles, hasError, isLoading }
  } = useContext(RecentVehiclesContext);
  const {
    pageAvailability: { data: availabilityData },
    pagePricing: { data: pricingData }
  } = useContext(AvailabilityAndPriceContext);

  const { values, carouselRef } = useCarouselSettings({
    heading: 'Recently Viewed',
    maxLength: recentVehicles.length
  });

  if (hasError || recentVehicles.length < 4) {
    return null;
  }

  if (isLoading)
    return <CarouselSkeleton includeSubHeader={false} Tile={LoadingTile} />;

  const customAttributes = {
    description: 'Recently Viewed',
    isCarousel: true,
    content: {
      vehicleIds: recentVehicles.map(({ vehicleId }) => vehicleId)
    }
  };

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes
  };

  function sendClickEvent(vehicleId: number) {
    trackEvent('Clicked - Home feed card', {
      description: 'Recently viewed tile',
      vehicleId
    });
  }
  const includesAnEvVehicle = includesEvVehicle(recentVehicles);
  const shouldCalculateEvWidth = someHaveAllDealTags(recentVehicles);

  return (
    <TrackWrapper event={viewEvent}>
      <Carousel {...values} ref={carouselRef} dataQa="recently-viewed">
        {recentVehicles.map(vehicle => {
          const { vehicleId } = vehicle;

          const tileAvailability = availabilityData[vehicleId];

          return (
            <BottomPaddingForBoxShadow
              key={vehicleId}
              includesEv={includesAnEvVehicle}
            >
              <VehicleTile
                availabilityInfo={{
                  pickup: tileAvailability?.soonestPickup?.content ?? undefined,
                  delivery:
                    tileAvailability?.soonestDelivery?.content ?? undefined
                }}
                financeInfo={pricingData[vehicleId] ?? undefined}
                vehicle={vehicle}
                sectionTitle="Recently Viewed"
                shouldCalculateEvWidth={shouldCalculateEvWidth}
                handleTileClick={() => sendClickEvent(vehicleId)}
              />
            </BottomPaddingForBoxShadow>
          );
        })}
      </Carousel>
    </TrackWrapper>
  );
}

export default withErrorBoundary(
  RecentlyViewed,
  undefined,
  undefined,
  Sections.RecentlyViewed
);
